/**
 * Word Timestamp Synchronization Utilities
 * 
 * Handles preserving and interpolating word timestamps during text editing
 */

/**
 * Normalize text for comparison (remove extra whitespace, lowercase)
 */
const normalizeText = (text) => {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Calculate similarity between two strings (simple Levenshtein-based approach)
 */
const calculateSimilarity = (str1, str2) => {
  const s1 = normalizeText(str1);
  const s2 = normalizeText(str2);
  
  if (s1 === s2) return 1.0;
  
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return 1.0;
  
  // Simple substring check
  if (longer.includes(shorter)) {
    return shorter.length / longer.length;
  }
  
  return 0.5; // Default moderate similarity
};

/**
 * Try to match edited words with original words to preserve timestamps
 */
export const preserveWordTimestamps = (editedText, originalWords) => {
  if (!originalWords || originalWords.length === 0) {
    return { words: [], needsInterpolation: true };
  }

  if (!editedText || !editedText.trim()) {
    return { words: [], needsInterpolation: false };
  }

  // Split edited text into words
  const editedWords = editedText.trim().split(/\s+/);
  const result = [];

  // Try to match each edited word with original words
  let originalIdx = 0;
  
  for (let i = 0; i < editedWords.length; i++) {
    const editedWord = editedWords[i];
    let bestMatch = null;
    let bestScore = 0;
    let bestMatchIdx = -1;

    // Look for best match in remaining original words (within reasonable window)
    const searchWindow = 5; // Look ahead up to 5 words
    for (let j = originalIdx; j < Math.min(originalIdx + searchWindow, originalWords.length); j++) {
      const originalWord = originalWords[j];
      const score = calculateSimilarity(editedWord, originalWord.word || originalWord.raw);
      
      if (score > bestScore) {
        bestScore = score;
        bestMatch = originalWord;
        bestMatchIdx = j;
      }
    }

    // If we found a good match (>70% similar), use its timestamp
    if (bestScore > 0.7 && bestMatch) {
      result.push({
        word: editedWord,
        raw: editedWord,
        start: bestMatch.start,
        end: bestMatch.end,
        speaker: bestMatch.speaker,
        probability: bestMatch.probability,
        index: i,
        preserved: true,
      });
      originalIdx = bestMatchIdx + 1;
    } else {
      // No good match found - will need interpolation
      result.push({
        word: editedWord,
        raw: editedWord,
        start: null,
        end: null,
        speaker: null,
        probability: null,
        index: i,
        preserved: false,
      });
    }
  }

  return { words: result, needsInterpolation: result.some(w => w.start === null) };
};

/**
 * Interpolate timestamps for words that don't have them
 */
export const interpolateTimestamps = (words, blockStart, blockEnd) => {
  if (!words || words.length === 0) return words;

  // Find words with timestamps
  const wordsWithTimestamps = words.filter(w => w.start !== null && w.end !== null);
  
  if (wordsWithTimestamps.length === 0) {
    // No timestamps at all - distribute evenly across block
    const totalDuration = blockEnd - blockStart;
    const wordDuration = totalDuration / words.length;
    
    return words.map((word, idx) => ({
      ...word,
      start: blockStart + (idx * wordDuration),
      end: blockStart + ((idx + 1) * wordDuration),
    }));
  }

  // Interpolate missing timestamps
  const result = [...words];
  
  for (let i = 0; i < result.length; i++) {
    if (result[i].start === null || result[i].end === null) {
      // Find nearest words with timestamps (before and after)
      let beforeIdx = -1;
      let afterIdx = -1;
      
      for (let j = i - 1; j >= 0; j--) {
        if (result[j].start !== null) {
          beforeIdx = j;
          break;
        }
      }
      
      for (let j = i + 1; j < result.length; j++) {
        if (result[j].start !== null) {
          afterIdx = j;
          break;
        }
      }

      // Interpolate based on surrounding words
      if (beforeIdx >= 0 && afterIdx >= 0) {
        // Between two known timestamps
        const beforeEnd = result[beforeIdx].end;
        const afterStart = result[afterIdx].start;
        const gap = afterStart - beforeEnd;
        const wordsInGap = afterIdx - beforeIdx;
        const wordDuration = gap / wordsInGap;
        const position = i - beforeIdx;
        
        result[i].start = beforeEnd + (position * wordDuration);
        result[i].end = beforeEnd + ((position + 1) * wordDuration);
      } else if (beforeIdx >= 0) {
        // After last known timestamp
        const lastEnd = result[beforeIdx].end;
        const remainingDuration = blockEnd - lastEnd;
        const wordsRemaining = result.length - beforeIdx - 1;
        const wordDuration = remainingDuration / wordsRemaining;
        const position = i - beforeIdx;
        
        result[i].start = lastEnd + (position * wordDuration);
        result[i].end = lastEnd + ((position + 1) * wordDuration);
      } else if (afterIdx >= 0) {
        // Before first known timestamp
        const firstStart = result[afterIdx].start;
        const durationBefore = firstStart - blockStart;
        const wordDuration = durationBefore / afterIdx;
        
        result[i].start = blockStart + (i * wordDuration);
        result[i].end = blockStart + ((i + 1) * wordDuration);
      }
    }
  }

  return result;
};

/**
 * Main function: synchronize words array with edited text
 * Preserves timestamps where possible, interpolates where necessary
 */
export const syncWordsWithText = (blockText, blockWords, blockStart, blockEnd) => {
  // First, try to preserve timestamps
  const { words, needsInterpolation } = preserveWordTimestamps(blockText, blockWords);
  
  // If interpolation is needed, do it
  if (needsInterpolation && blockStart !== undefined && blockEnd !== undefined) {
    return interpolateTimestamps(words, blockStart, blockEnd);
  }
  
  return words;
};

/**
 * Check if text has changed significantly from words array
 */
export const hasTextChanged = (blockText, blockWords) => {
  if (!blockWords || blockWords.length === 0) return true;
  
  const reconstructedText = blockWords
    .map(w => w.word || w.raw)
    .join(' ')
    .trim();
  
  const normalizedBlock = normalizeText(blockText);
  const normalizedReconstructed = normalizeText(reconstructedText);
  
  return normalizedBlock !== normalizedReconstructed;
};


